import { useEffect, useRef, type RefObject } from "react";

import { LiquidGlass } from "./LiquidGlass";
import { useMotionPolicy } from "./motionPolicy";
import "./Motion.css";
import "./SelectionIndicator.css";

const itemSelector = "[data-ark-selection-item]";
const activeSelector =
  '[aria-selected="true"], [aria-current="page"], [aria-pressed="true"]';

interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One moving decoration; the actual links/buttons retain focus and semantics. */
export function SelectionIndicator({
  rootRef,
  selector = activeSelector,
  variant = "surface",
  tone = "soft",
  followInteraction = false,
}: {
  rootRef: RefObject<HTMLElement | null>;
  selector?: string;
  variant?: "surface" | "underline";
  tone?: "soft" | "primary";
  followInteraction?: boolean;
}) {
  const indicator = useRef<HTMLElement>(null);
  const policy = useMotionPolicy();

  // Parent refs are attached after child layout effects on the first commit.
  useEffect(() => {
    const root = rootRef.current;
    const layer = indicator.current;
    if (!root || !layer) return;
    root.dataset.arkSelectionScope = "";
    root.dataset.arkSelectionVariant = variant;
    if (followInteraction) root.dataset.arkSelectionFollow = "";
    let frame = 0;
    let animation: Animation | undefined;
    let previous: SelectionRect | undefined;
    let interaction: HTMLElement | undefined;
    const observed = new Set<HTMLElement>();
    const owns = (item: HTMLElement) =>
      item.closest("[data-ark-selection-scope]") === root;
    const visible = (item: HTMLElement) =>
      owns(item) &&
      !item.matches(':disabled, [aria-disabled="true"]') &&
      !item.closest("[hidden], [inert]") &&
      item.getClientRects().length > 0;
    // Account for the popover's entry scale as well as scrolling and borders.
    const rectWithinRoot = (element: HTMLElement): SelectionRect => {
      const bounds = root.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const scaleX = bounds.width / root.offsetWidth || 1;
      const scaleY = bounds.height / root.offsetHeight || 1;
      return {
        x:
          (rect.left - bounds.left) / scaleX +
          root.scrollLeft -
          root.clientLeft,
        y: (rect.top - bounds.top) / scaleY + root.scrollTop - root.clientTop,
        width: rect.width / scaleX,
        height: rect.height / scaleY,
      };
    };
    const measure = () => {
      frame = 0;
      const items = Array.from(
        root.querySelectorAll<HTMLElement>(itemSelector),
      );
      const target =
        (interaction && visible(interaction) ? interaction : undefined) ??
        items.find((item) => item.matches(selector) && visible(item));
      if (!target) {
        animation?.cancel();
        animation = undefined;
        previous = undefined;
        layer.style.visibility = "hidden";
        delete root.dataset.arkSelectionReady;
        return;
      }
      const next = rectWithinRoot(target);
      if (variant === "underline") {
        next.y += next.height - 3;
        next.height = 3;
      }
      if (next.width <= 0 || next.height <= 0) return;
      if (
        previous &&
        Object.keys(next).every(
          (key) =>
            Math.abs(
              next[key as keyof SelectionRect] -
                previous![key as keyof SelectionRect],
            ) < 0.5,
        )
      )
        return;
      // Capture the rendered intermediate position before cancelling a rapid switch.
      const start = previous ? rectWithinRoot(layer) : undefined;
      animation?.cancel();
      animation = undefined;
      const transform = `translate(${next.x}px, ${next.y}px)`;
      layer.style.width = `${next.width}px`;
      layer.style.height = `${next.height}px`;
      layer.style.transform = transform;
      layer.style.visibility = "visible";
      root.dataset.arkSelectionReady = "true";
      if (start && policy.enabled && layer.animate) {
        animation = layer.animate(
          policy.distance
            ? [
                {
                  transform: `translate(${start.x}px, ${start.y}px) scale(${start.width / next.width}, ${start.height / next.height})`,
                },
                { transform },
              ]
            : [{ opacity: 0.4 }, { opacity: 1 }],
          { duration: policy.duration, easing: policy.easing },
        );
      }
      previous = next;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    const observeItems = () => {
      const items = new Set(
        [
          ...Array.from(root.querySelectorAll<HTMLElement>(itemSelector)),
          ...Array.from(root.children).filter(
            (child): child is HTMLElement =>
              child instanceof HTMLElement && child !== layer,
          ),
        ].filter(owns),
      );
      for (const item of observed) {
        if (!items.has(item)) {
          resize.unobserve(item);
          observed.delete(item);
        }
      }
      for (const item of items) {
        if (!observed.has(item)) {
          observed.add(item);
          resize.observe(item);
        }
      }
    };
    const mutation = new MutationObserver((records) => {
      if (
        records.every(
          (record) => layer === record.target || layer.contains(record.target),
        )
      )
        return;
      observeItems();
      schedule();
    });
    mutation.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "aria-selected",
        "aria-current",
        "aria-pressed",
        "aria-disabled",
        "data-selected",
        "class",
        "disabled",
        "hidden",
        "inert",
      ],
    });
    const interact = (event: Event) => {
      const item =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(itemSelector)
          : null;
      if (item && visible(item)) {
        interaction = item;
        schedule();
      }
    };
    const leave = () => {
      const focused =
        document.activeElement instanceof Element
          ? document.activeElement.closest<HTMLElement>(itemSelector)
          : null;
      interaction = focused && visible(focused) ? focused : undefined;
      schedule();
    };
    if (followInteraction) {
      root.addEventListener("focusin", interact);
      root.addEventListener("focusout", leave);
      root.addEventListener("pointermove", interact, { passive: true });
      root.addEventListener("pointerleave", leave);
    }
    root.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    observeItems();
    measure();
    return () => {
      resize.disconnect();
      mutation.disconnect();
      cancelAnimationFrame(frame);
      animation?.cancel();
      root.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      root.removeEventListener("focusin", interact);
      root.removeEventListener("focusout", leave);
      root.removeEventListener("pointermove", interact);
      root.removeEventListener("pointerleave", leave);
      delete root.dataset.arkSelectionScope;
      delete root.dataset.arkSelectionVariant;
      delete root.dataset.arkSelectionFollow;
      delete root.dataset.arkSelectionReady;
    };
  }, [
    rootRef,
    selector,
    variant,
    followInteraction,
    policy.enabled,
    policy.duration,
    policy.distance,
    policy.easing,
  ]);

  return (
    <LiquidGlass
      aria-hidden="true"
      inert
      adaptation="none"
      className="ark-selection-indicator"
      data-variant={variant}
      data-tone={tone}
      ref={indicator}
    />
  );
}
